import { useState } from 'react';
import { addDays } from 'date-fns';
import { toDate } from 'date-fns-tz';
import api from 'api';
import { useDateNow, useDispatch, useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { Alert, ModalButtonWithForm } from 'components/common';
import {
  Datepicker,
  EmailMessageEditor,
  FormGroup,
  RadioGroup,
  UserSelector
} from 'components/common/form';
import { UserPasswordReview } from 'components/users';

import type { EmailTemplateData } from 'shared-types';
import type { UpdateShareUserObject } from './ShareUsers';

interface Props {
  emailTemplates: EmailTemplateData[];
  emailTemplatesDefault: EmailTemplateData;
  updateCourse: () => Promise<void>;
  updateUsers: (updateObject: UpdateShareUserObject) => Promise<void>;
  userIds: number[];
}

const ShareUsersAdd = ({
  emailTemplates,
  emailTemplatesDefault,
  updateCourse,
  updateUsers,
  userIds
}: Props) => {
  const { dateNow } = useDateNow();

  const {
    messages,
    pageState: { course },
    serverConfig: {
      accountAddForceExpirationDate,
      accountAddForceExpirationDateMaxDays,
      timeZone
    }
  } = useReduxState();

  const { dispatch } = useDispatch();

  const [newUsers, setNewUsers] = useState([]);
  const [userPasswords, setUserPasswords] = useState({});
  const [userSelector, setUserSelector] = useState('emails');

  const handleHide = () => {
    setNewUsers([]);
    setUserPasswords({});
  };

  const handleNewUsers = (newUsersValue) => {
    setNewUsers(newUsersValue);
  };

  const handleUserSelectorChange = (value) => {
    setUserSelector(value);
  };

  const handleSubmit = async (values) => {
    const data = {
      emails: values.emails,
      expirationInDays: values.expirationInDays,
      passwords: values.passwords,
      userExpirationDate: values.userExpirationDate
        ? toDate(values.userExpirationDate, { timeZone })
        : null,
      usernames: values.usernames,
      ...(values.emails && { emailContent: values.email }),
      ...(values.emails && { recipientsType: values.recipientsType })
    };

    const response = await api.post(`/admin/courses/${course.id}/users`, data);
    let successMessage =
      `${response.data.addedUsers} ${messages.settingsAddUsersUsersAdded}, ` +
      `${response.data.newUsers} ${messages.settingsAddUsersAccountsCreated}`;

    let isWarning = false;
    const errorEmails = Object.keys(response.data.errors);

    if (errorEmails && errorEmails.length) {
      isWarning = true;
      successMessage += `<br /><br />${errorEmails.length} ${messages.settingsAddUsersErrors}:`;
      errorEmails.forEach((errorEmail) => {
        const error = response.data.errors[errorEmail];
        const errorMessage =
          error === 'email_inactive_address'
            ? messages.settingsAddUsersErrorInvalidRecipient
            : error === 'email'
            ? messages.settingsAddUsersErrorEmail
            : messages.settingsAddUsersErrorOther;
        successMessage += `<br />${errorEmail}: ${errorMessage}`;
      });
    }

    await updateUsers({});
    await updateCourse();

    dispatch.addNotice({
      text: successMessage,
      type: isWarning ? 'warning' : 'success'
    });

    if (values.usernames?.length && response.data.newUsers) {
      const userPasswords = {};
      values.passwords.forEach((password, i) => {
        if (password) userPasswords[values.usernames[i]] = password;
      });

      setUserPasswords(userPasswords);
    }
  };

  const initialValues = {
    emails: [],
    email: {
      ...emailTemplatesDefault.email,
      selectedTemplateIdentifier: emailTemplatesDefault.identifier
    },
    recipientsType: 'all',
    userExpirationDate: null,
    usernames: []
  };

  return (
    <>
      <ModalButtonWithForm
        buttonCategory="success"
        buttonIcon="plus"
        buttonSize="header"
        buttonText={messages.commonAddUsers}
        buttonTextHideBreakpoint={breakpoints.small - 1}
        formAllowEmptyStrings={true}
        initialValues={initialValues}
        modalActionText={messages.commonSubmit}
        modalTitle={messages.commonAddUsers}
        modalWidth="wide"
        onSubmit={handleSubmit}
      >
        {() => (
          <div>
            {!course.startDate ? (
              <Alert
                style={{ marginBottom: 15 }}
                text={messages.shareNotPublishedWarning}
                type="error-light"
              />
            ) : null}

            <UserSelector
              existingUsers={userIds}
              onNewUsers={handleNewUsers}
              onUserSelectorChange={handleUserSelectorChange}
            />

            {accountAddForceExpirationDate && newUsers.length > 0 && (
              <FormGroup
                htmlFor="userExpirationDate"
                label={messages.commonExpirationDateForNewUserAccounts}
                maxWidth="100%"
              >
                <Datepicker
                  field="userExpirationDate"
                  id="userExpirationDate"
                  isClearable={false}
                  maxDate={addDays(
                    dateNow,
                    accountAddForceExpirationDateMaxDays
                  )}
                  minDate={dateNow}
                  required={true}
                  validateOnChange={true}
                  validateOnBlur={true}
                  style={{ maxWidth: 200 }}
                  placeholder={messages.commonExpirationDate}
                />
              </FormGroup>
            )}

            {userSelector === 'emails' ? (
              <>
                <FormGroup label={messages.emailRecipientsSend}>
                  <RadioGroup
                    field="recipientsType"
                    options={[
                      {
                        id: 'email-recipients-all',
                        label: messages.emailRecipientsAllAbove,
                        value: 'all'
                      },
                      {
                        id: 'email-recipients-new',
                        label: messages.emailRecipientsNew,
                        value: 'new'
                      }
                    ]}
                  />
                </FormGroup>

                <EmailMessageEditor
                  availableTemplates={emailTemplates}
                  id="share-users-add-send-message"
                  style={{ marginTop: 20 }}
                />
              </>
            ) : null}
          </div>
        )}
      </ModalButtonWithForm>

      <UserPasswordReview
        onModalDismiss={handleHide}
        passwords={userPasswords}
      />
    </>
  );
};

export default ShareUsersAdd;
